import { ResponsiveTable } from '../components/ResponsiveTable';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Plus,
  Layers,
  AlertCircle,
  Loader2,
  Trash2,
  Edit,
  Link2,
} from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { RootState } from '../store';
import {
  ItemCollection,
  listItemCollections,
  createItemCollection,
  updateItemCollection,
  deleteItemCollection,
} from '../services/itemCollectionsApi';

export function ItemCollectionsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const canHardDelete = user?.role === 'super_admin';

  const [collections, setCollections] = useState<ItemCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ItemCollection | null>(null);
  const limit = 20;

  const fetchCollections = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listItemCollections({
        page,
        limit,
        search: search || undefined,
        sortKey: 'name',
        sortDir: 'asc',
      });
      setCollections(data.itemCollections);
      setTotal(data.total);
    } catch {
      setError(t('catalog.itemCollections.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, [search, page]);

  const handleDelete = async (id: string, hard = false) => {
    if (!window.confirm(t('catalog.itemCollections.confirmDelete'))) return;
    try {
      await deleteItemCollection(id, hard);
      fetchCollections();
    } catch (err: any) {
      setError(err?.message || t('catalog.itemCollections.deleteError'));
    }
  };

  if (loading && collections.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="dt-list-toolbar mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
            <span>{t('catalog.itemCollections.title')}</span>
            <HelpIcon docPath="10-donnees-reference#10-1-zones-corps" tooltip={t('catalog.itemCollections.title')} />
          </h1>
          <p className="text-textSecondary mt-1">{t('catalog.itemCollections.subtitle', { count: total })}</p>
        </div>
        <button onClick={() => { setEditing(null); setShowModal(true); }} className="btn-primary flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>{t('catalog.itemCollections.add')}</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={t('catalog.itemCollections.search')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="input-field pl-10"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
          <span className="text-red-800 text-sm">{error}</span>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <TableScrollContainer hint="→">
          <ResponsiveTable className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('catalog.itemCollections.code')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('catalog.itemCollections.name')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('catalog.itemCollections.zones')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('catalog.itemCollections.types')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('catalog.itemCollections.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {collections.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-mono">{c.code}</td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{c.name}</td>
                  <td className="px-6 py-4 text-sm">{c.bodyZoneCount ?? 0}</td>
                  <td className="px-6 py-4 text-sm">{c.piercingTypeCount ?? 0}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => navigate(`/item-collections/${c.id}/associations`)}
                        className="text-primary-600 p-1"
                        title={t('catalog.itemCollections.associations')}
                      >
                        <Link2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => { setEditing(c); setShowModal(true); }} className="text-primary-600 p-1">
                        <Edit className="w-4 h-4" />
                      </button>
                      {c.code !== 'DEFAULT' && (
                        <button onClick={() => handleDelete(c.id, false)} className="text-red-600 p-1">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      {canHardDelete && c.code !== 'DEFAULT' && (
                        <button onClick={() => handleDelete(c.id, true)} className="text-red-800 p-1" title={t('catalog.itemCollections.hardDelete')}>
                          <AlertCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>
        {!loading && collections.length === 0 && (
          <div className="text-center py-12">
            <Layers className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">{t('catalog.itemCollections.empty')}</p>
          </div>
        )}
      </div>

      {showModal && (
        <ItemCollectionModal
          collection={editing}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSuccess={() => { setShowModal(false); setEditing(null); fetchCollections(); }}
        />
      )}
    </div>
  );
}

function ItemCollectionModal({
  collection,
  onClose,
  onSuccess,
}: {
  collection: ItemCollection | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    code: collection?.code || '',
    name: collection?.name || '',
    description: collection?.description || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isDefault = collection?.code === 'DEFAULT';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      if (collection) {
        await updateItemCollection(collection.id, formData);
      } else {
        await createItemCollection(formData);
      }
      onSuccess();
    } catch (err: any) {
      setError(err?.message || t('catalog.itemCollections.saveError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold mb-4">
          {collection ? t('catalog.itemCollections.edit') : t('catalog.itemCollections.add')}
        </h2>
        {error && <p className="text-red-700 text-sm mb-3">{error}</p>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">{t('catalog.itemCollections.code')} *</label>
            <input
              required
              disabled={isDefault}
              value={formData.code}
              onChange={(e) => setFormData((p) => ({ ...p, code: e.target.value }))}
              className="input-field"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">{t('catalog.itemCollections.name')} *</label>
            <input
              required
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              className="input-field"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">{t('catalog.itemCollections.description')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
              className="input-field"
              rows={3}
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={onClose} className="btn-secondary">{t('catalog.itemCollections.cancel')}</button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? t('catalog.itemCollections.saving') : t('catalog.itemCollections.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
